import { useCallback, useContext, useReducer, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CrudMode, Page } from '@ohpen/enso-ui';
import { FirmsList, FirmsCrudForm, FIRMS_ENTITY_LIST_NAMES, ConfirmCrDeleteModal, ChangeLogDrawer } from '@pmp/components';
import { ActionEntityType, FirmGetAllResponse, firmsValidateDelete, firmsCreateDeleteCr } from '@pmp/api';
import { useValueListsStore } from '@pmp/stores';
import { PmpConfigContext } from '@pmp/contexts';
import { usePmpFetch } from '@pmp/hooks';

import { initialState, reducer } from './reducer';

export const FirmsPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('firmsGetAllFirms');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { brokerPortalUrl } = useContext(PmpConfigContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    getValueLists({ alwaysFetch: FIRMS_ENTITY_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onSeeFirmCases = useCallback(
    (record: FirmGetAllResponse) => {
      if (!brokerPortalUrl || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/broker/cases?firmContext=${record.ohpenId}`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onManageFirmCases = useCallback(
    (record: FirmGetAllResponse) => {
      if (!brokerPortalUrl || !record?.ohpenId) return;
      window.open(`${brokerPortalUrl}/sso/auth?firmContext=${record?.ohpenId}`, '_blank');
    },
    [brokerPortalUrl],
  );

  const onSeeFirmIntermediaries = useCallback(
    (record: FirmGetAllResponse) => {
      if (!record?.name) return;
      navigate(`/brokers?search-${ActionEntityType.BROKERS}=${encodeURIComponent(record.name)}`);
    },
    [navigate],
  );

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: FirmGetAllResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const onViewChangeLog = useCallback((record: FirmGetAllResponse) => {
    dispatch({
      type: 'openChangeLog',
      changeLog: {
        target: { entityType: ActionEntityType.FIRMS, id: record.id, name: record.name },
      },
    });
  }, []);

  const onCloseChangeLog = useCallback(() => {
    dispatch({ type: 'closeChangeLog' });
  }, []);

  return (
    <Page>
      <Page.Title>{t('firms.title')}</Page.Title>
      <Page.Content>
        <FirmsList
          isFetching={isFetching}
          data={data ?? []}
          valueLists={valueLists}
          onCreate={onCreate}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onSeeFirmCases={onSeeFirmCases}
          onManageFirmCases={onManageFirmCases}
          onSeeFirmIntermediaries={onSeeFirmIntermediaries}
          onViewChangeLog={onViewChangeLog}
        />
        {state.crud.open && state.crud.mode && (
          <FirmsCrudForm
            valueLists={valueLists}
            mode={state.crud.mode}
            record={state.crud.record}
            refreshTable={fetchData}
            onClose={onCloseCrud}
          />
        )}
        <ChangeLogDrawer target={state.changeLog.target} onClose={onCloseChangeLog} />
        <ConfirmCrDeleteModal
          open={state.deleteModal.open}
          records={state.deleteModal.record ? [state.deleteModal.record] : []}
          idKey="id"
          nameKey="name"
          entityLabel={t('firms.labels.firm')}
          entityLabelPlural={t('firms.labels.firms')}
          getPrecheck={firmsValidateDelete}
          createDeleteChangeRequest={firmsCreateDeleteCr}
          refreshTable={fetchData}
          onClose={onCloseDeleteModal}
        />
      </Page.Content>
    </Page>
  );
};
